import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import { Person } from "@/types";
import {
  buildLevelMetric,
  getPriorProgramCompletionTooltip,
  getProspectAssociateProgramDeadline,
  getQualifyingLevelMetricInput,
  isQualifyingLevelComplete,
  type LevelMetricInput
} from "./pgm-levels";
import { isRequirementRowSatisfied, type RequirementRow } from "./pgm-requirements";
import {
  buildQualifyingLevelRequirements,
  getCompletedQualifyingApplication,
  isActivelyFinishingQualifyingLevel,
  isOpenPurchaseLevelOnePath
} from "./qualifyingLevelRequirements";

const QUALIFYING_LEVEL_VALUE = "Qualifying Level";

export { getQualifyingLevelMetricInput } from "./pgm-levels";

export function incompleteQualifyingLevelCardLabels(
  person: Person,
  requirements: RequirementRow[] = buildQualifyingLevelRequirements(person)
): string[] {
  const completed = getCompletedQualifyingApplication(person);
  const omitLeftoverOpenPurchase = isOpenPurchaseLevelOnePath(person, completed);
  const missing = requirements
    .filter((row) => {
      if (omitLeftoverOpenPurchase && (row.key === "purchase" || /^Purchase Level 1/i.test(row.name))) {
        return false;
      }
      return !isRequirementRowSatisfied(row);
    })
    .map((row) => row.name);

  if (isQualifyingLevelComplete(person) && !isActivelyFinishingQualifyingLevel(person, completed)) {
    return [];
  }

  return missing;
}

export function isQualifyingLevelCardComplete(person: Person, requirements?: RequirementRow[]): boolean {
  return incompleteQualifyingLevelCardLabels(person, requirements).length === 0;
}

function mergeMissingRequirementsTooltip(
  existingTooltip: MetricCardType<Path>["tooltip"],
  missing: string[]
): MetricCardType<Path>["tooltip"] {
  if (missing.length === 0) return existingTooltip;

  const supportingText = `Still needed: ${missing.join(", ")}`;
  if (!existingTooltip) return { supportingText };

  return {
    ...existingTooltip,
    supportingText: existingTooltip.supportingText
      ? `${supportingText}\n${existingTooltip.supportingText}`
      : supportingText
  };
}

function cardInputWhileFinishing(person: Person, input: LevelMetricInput): LevelMetricInput {
  const prospectDeadline = getProspectAssociateProgramDeadline(person);

  return {
    ...input,
    isCompleted: false,
    expirationDate: prospectDeadline ?? input.expirationDate ?? null
  };
}

/** Leftover locked Purchase Level 1 can keep progress `in_progress`; a complete card still uses F0 / prospect end dates. */
function cardInputWhenComplete(person: Person, input: LevelMetricInput): LevelMetricInput {
  if (input.isCompleted) return input;

  const completedF0 = [...(person.educationPgmProgramHistory ?? [])]
    .filter((item) => item.level === 0 && item.completed)
    .sort((a, b) => (b.endDate ?? "").localeCompare(a.endDate ?? ""))[0];
  const prospectQl = person.prospectProgram?.qualifyingLevel;
  const prospectCompleted =
    !!prospectQl && (!!prospectQl.completed || prospectQl.status === "COMPLETED" || !!prospectQl.endDate);

  return {
    ...input,
    startDate: completedF0?.startDate ?? (prospectCompleted ? prospectQl.startDate : null) ?? input.startDate,
    completionDate: completedF0?.endDate ?? (prospectCompleted ? prospectQl.endDate : null) ?? null,
    expirationDate: null,
    isCompleted: true
  };
}

export function buildQualifyingLevelMetric(
  person: Person,
  isSuspended: boolean,
  requirements: RequirementRow[] = buildQualifyingLevelRequirements(person)
): MetricCardType<Path> {
  const input = getQualifyingLevelMetricInput(person);
  const missing = incompleteQualifyingLevelCardLabels(person, requirements);

  if (!input) {
    const base = buildLevelMetric(input, QUALIFYING_LEVEL_VALUE, isSuspended);
    return {
      ...base,
      value: QUALIFYING_LEVEL_VALUE,
      tooltip: undefined
    };
  }

  if (missing.length === 0) {
    const completedInput = cardInputWhenComplete(person, input);
    const base = buildLevelMetric(completedInput, QUALIFYING_LEVEL_VALUE, isSuspended);
    const priorProgramTooltip = getPriorProgramCompletionTooltip(person, completedInput);
    return {
      ...base,
      value: QUALIFYING_LEVEL_VALUE,
      variant: "success",
      ...(priorProgramTooltip ? { tooltip: priorProgramTooltip } : {})
    };
  }

  const base = buildLevelMetric(cardInputWhileFinishing(person, input), QUALIFYING_LEVEL_VALUE, isSuspended);

  return {
    ...base,
    value: QUALIFYING_LEVEL_VALUE,
    variant: base.variant === "error" ? "error" : base.label === "Not Started" ? undefined : "info",
    tooltip: mergeMissingRequirementsTooltip(base.tooltip, missing)
  };
}
